import { DISTANCE_STATE_CODES, type DistanceTripsQuery } from '@chukua/common';
import { Tag, type TableColumnsType } from 'antd';
import { Link } from 'react-router';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import type { DistanceTripItem } from '../distance-agreement.api';
import { closeOutTripPath } from '../distance-agreement.paths';
import { formatKm, formatPercent, gapUnavailable, gpsUnavailable, odometerUnavailable, stateTag } from '../policies/distance-labels.policy';
import { DistanceFigure } from './DistanceFigure';

type Controls = Pick<ListQueryControls<DistanceTripsQuery>, 'sortOrderFor' | 'filteredValueFor'>;
type Column = TableColumnsType<DistanceTripItem>[number];

const km = (value: number | null) => (value === null ? null : formatKm(value));

const tripColumn = (list: Controls): Column => ({
  title: 'Trip',
  key: 'trip',
  sorter: true,
  sortOrder: list.sortOrderFor('trip'),
  render: (_, item) => (
    <Link to={closeOutTripPath(item.tripId)} className="flex flex-col py-1">
      <span className="font-semibold">{item.number}</span>
      <span className="text-xs text-muted">{item.truckRegistration ?? 'No truck'}</span>
    </Link>
  ),
});

const coverageNote = (item: DistanceTripItem) =>
  item.gpsCoveragePct === null ? undefined : `${Math.round(item.gpsCoveragePct)}% coverage${item.isGpsLowCoverage ? ' (a lower bound)' : ''}`;

const distanceColumns = (list: Controls): Column[] => [
  { title: 'Odometer', key: 'odometer', render: (_, item) => <DistanceFigure value={km(item.odometerDistanceKm)} unavailable={odometerUnavailable(item)} /> },
  {
    title: 'GPS',
    key: 'gps',
    render: (_, item) => <DistanceFigure value={km(item.gpsDistanceKm)} unavailable={gpsUnavailable(item)} note={coverageNote(item)} />,
  },
  {
    title: 'Gap',
    key: 'gapKm',
    sorter: true,
    sortOrder: list.sortOrderFor('gapKm'),
    render: (_, item) => (
      <DistanceFigure value={km(item.gapKm)} unavailable={gapUnavailable(item)} note={item.gapPercent === null ? undefined : formatPercent(item.gapPercent)} />
    ),
  },
];

const stateColumn = (list: Controls, hasFilter: boolean): Column => ({
  title: 'State',
  key: 'state',
  ...(hasFilter
    ? { filters: DISTANCE_STATE_CODES.map((state) => ({ text: stateTag(state).label, value: state })), filteredValue: list.filteredValueFor('state') }
    : {}),
  render: (_, item) => {
    const tag = stateTag(item.state);
    return <Tag color={tag.color}>{tag.label}</Tag>;
  },
});

const closedOutColumn = (list: Controls): Column => ({
  title: 'Closed out',
  key: 'closedOutAt',
  sorter: true,
  sortOrder: list.sortOrderFor('closedOutAt'),
  render: (_, item) => new Date(item.closedOutAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }),
});

/** Sorting and filtering run on the server: each column's `key` is its `sort` field or filter param in `distanceTripsQuerySchema`. The state filter is offered only where the tab leaves the state open. */
export function buildCloseOutColumns(list: Controls, options: { hasStateFilter: boolean }): TableColumnsType<DistanceTripItem> {
  return [tripColumn(list), ...distanceColumns(list), stateColumn(list, options.hasStateFilter), closedOutColumn(list)];
}
